import React from 'react';
import {
	FormattedMessage,
	defineMessages,
} from 'react-intl';

import DoubleArrowIcon from '~/components/app/DoubleArrowIcon';
import Button, {
	ButtonWidth,
} from '~/components/patterns/buttons/Button';
import {
	useSelectedAccount,
} from '~/contexts/SelectedAccountContext';
import useNavigationLayout from '~/hooks/useNavigationLayout';



const messages = defineMessages({
	switchToNewMenu: {
		id: 'ui.navigation.button.switchToNewMenu',
		defaultMessage: 'Switch to new menu',
	},
	switchToClassicMenu: {
		id: 'ui.navigation.button.switchToClassicMenu',
		defaultMessage: 'Switch to classic menu',
	},
});

/**
 * Navigation Layout Toggle Component
 *
 * Renders a button that allows users to switch between old (purple) and new (black)
 * navigation layouts during the migration period.
 *
 * Button states:
 * - When on 'old' layout → green button "Switch to new menu" (or arrow icon in Phase 2)
 * - When on 'new' layout → purple button "Switch to classic menu" (or arrow icon in Phase 2)
 *
 * Responsive behavior:
 * - Phase 2 (mobile/responsive): Shows arrow icon only
 * - Phase 3 (desktop ≥1169px): Shows text label
 */
const NavigationLayoutToggle: React.FC = () => {
	const [
		currentLayout,
		setLayout,
		showToggle,
	] = useNavigationLayout();
	const {
		setSelectedAccountId,
	} = useSelectedAccount();

	// Determine button content based on viewport
	const isOldLayout = currentLayout === 'old';

	// Always render both icon and text - CSS will control visibility based on viewport
	// New nav: 20px; old nav: 24px (keep old nav untouched)
	const buttonIcon = (
		<DoubleArrowIcon
			color="#ffffff"
			size={isOldLayout ? 24 : 20}
		/>
	);
	const buttonText = isOldLayout
		? <FormattedMessage {...messages.switchToNewMenu} />
		: <FormattedMessage {...messages.switchToClassicMenu} />;

	const handleClick = React.useCallback(
		() => {
			const newLayout = isOldLayout ? 'new' : 'old';
			setLayout(newLayout);
			// Clear selected account when switching to old nav so TeamWebsitesDropdown
			// (and other consumers) don't use a stale context value
			if (newLayout === 'old') {
				setSelectedAccountId(null);
			}
		},
		[isOldLayout, setLayout, setSelectedAccountId],
	);

	// Don't render if button should be hidden (based on migration period)
	if (!showToggle) {
		return null;
	}

	return (
		<Button
			className="navigation-layout-toggle-button"
			icon={buttonIcon}
			onClick={handleClick}
			width={ButtonWidth.Fullwidth}
		>
			{buttonText}
		</Button>
	);
};

export default NavigationLayoutToggle;
